import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'
import moment from 'moment'
import {
  OVERALL_SERIES_COLOR,
  TREND_DAILY_AXIS_MAX_DAYS,
  TREND_SERIES_COLORS,
  TimeRange,
} from '../core/_const'
import {ShipmentTrendPoint, ShipmentTrendSeries} from '../core/_types'
import {
  buildTrendSeriesColorMap,
  getTrendSeriesKey,
  isMultiSeriesBreakout,
  ShipmentTrendBreakout,
} from './organizationAnalyticsHelpers'

const AXIS_LABEL_STYLE = {
  fill: am5.color('#94A3B8'),
  fontSize: 12,
  fontWeight: '500' as const,
}

const shouldUseDailyTrendAxis = (
  timeRange: TimeRange,
  isCustomRange: boolean,
  dateRange: {start: moment.Moment; end: moment.Moment}
) => {
  if (!isCustomRange) {
    return timeRange === 'wtd' || timeRange === 'mtd'
  }

  const rangeDays =
    dateRange.end.clone().endOf('day').diff(dateRange.start.clone().startOf('day'), 'days') + 1

  return rangeDays <= TREND_DAILY_AXIS_MAX_DAYS
}

const getShipmentTrendsAxisConfig = (
  timeRange: TimeRange,
  isCustomRange: boolean,
  dateRange: {start: moment.Moment; end: moment.Moment}
) => {
  const useDailyAxis = shouldUseDailyTrendAxis(timeRange, isCustomRange, dateRange)

  if (useDailyAxis) {
    return {
      baseInterval: {timeUnit: 'day' as const, count: 1},
      dateFormats: {
        day: 'MMM d',
        week: 'MMM d',
        month: 'MMM d',
        year: 'MMM d',
      },
      minGridDistance: 40,
    }
  }

  return {
    baseInterval: {timeUnit: 'month' as const, count: 1},
    dateFormats: {
      day: 'MMM yyyy',
      week: 'MMM yyyy',
      month: 'MMM yyyy',
      year: 'MMM yyyy',
    },
    minGridDistance: 60,
  }
}

const addBreakoutSeries = (
  root: am5.Root,
  chart: am5xy.XYChart,
  xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
  yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
  seriesData: ShipmentTrendSeries[]
) => {
  const colorMap = buildTrendSeriesColorMap(seriesData)

  seriesData.forEach((breakoutSeries, index) => {
    const color =
      colorMap[getTrendSeriesKey(breakoutSeries)] ??
      TREND_SERIES_COLORS[index % TREND_SERIES_COLORS.length]
    const series = chart.series.push(
      am5xy.SmoothedXLineSeries.new(root, {
        name: breakoutSeries.label,
        xAxis,
        yAxis,
        valueXField: 'label',
        valueYField: 'value',
        stroke: am5.color(color),
        tooltip: am5.Tooltip.new(root, {
          labelText: '{name}: {valueY.formatNumber("#,###")}',
        }),
      })
    )

    series.strokes.template.setAll({strokeWidth: 2})
    series.fills.template.setAll({visible: false})
    series.data.processor = am5.DataProcessor.new(root, {
      dateFormat: 'yyyy-MM-dd',
      dateFields: ['label'],
    })
    series.data.setAll(breakoutSeries.data)
    series.appear(800)
  })
}

const addOverallSeries = (
  root: am5.Root,
  chart: am5xy.XYChart,
  xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
  yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
  data: ShipmentTrendPoint[],
  shipmentsLabel: string
) => {
  const tooltip = am5.Tooltip.new(root, {
    labelText: '{valueY.formatNumber("#,###")}',
  })
  tooltip.get('background')?.setAll({
    fill: am5.color(OVERALL_SERIES_COLOR),
    fillOpacity: 0.8,
  })

  const series = chart.series.push(
    am5xy.SmoothedXLineSeries.new(root, {
      name: shipmentsLabel,
      xAxis,
      yAxis,
      valueXField: 'label',
      valueYField: 'value',
      tooltip,
      stroke: am5.color(OVERALL_SERIES_COLOR),
    })
  )

  series.strokes.template.setAll({strokeWidth: 3})
  series.fills.template.setAll({visible: true, fillOpacity: 0.15})
  series.fills.template.set(
    'fillPattern',
    am5.LinePattern.new(root, {
      color: am5.color('#F0FDFA'),
      fill: am5.color('#F0FDFA'),
    })
  )
  series.data.processor = am5.DataProcessor.new(root, {
    dateFormat: 'yyyy-MM-dd',
    dateFields: ['label'],
  })
  series.data.setAll(data)
  series.appear(800)
}

export const createShipmentTrendsChart = ({
  chartId,
  activeView,
  overallData,
  seriesData,
  shipmentsLabel,
  timeRange,
  isCustomRange,
  dateRange,
}: {
  chartId: string
  activeView: ShipmentTrendBreakout
  overallData: ShipmentTrendPoint[]
  seriesData: ShipmentTrendSeries[]
  shipmentsLabel: string
  timeRange: TimeRange
  isCustomRange: boolean
  dateRange: {start: moment.Moment; end: moment.Moment}
}) => {
  const root = am5.Root.new(chartId)
  root.setThemes([am5themes_Animated.new(root)])
  root._logo?.dispose()

  const chart = root.container.children.push(
    am5xy.XYChart.new(root, {
      width: am5.percent(100),
      paddingTop: 8,
      paddingBottom: 8,
      paddingLeft: 0,
      paddingRight: 8,
    })
  )
  chart.root.dom.style.width = '100%'
  chart.root.dom.style.height = '260px'

  chart.set(
    'cursor',
    am5xy.XYCursor.new(root, {
      behavior: 'none',
    })
  )

  const axisConfig = getShipmentTrendsAxisConfig(timeRange, isCustomRange, dateRange)

  const xAxis = chart.xAxes.push(
    am5xy.DateAxis.new(root, {
      baseInterval: axisConfig.baseInterval,
      renderer: am5xy.AxisRendererX.new(root, {
        minGridDistance: axisConfig.minGridDistance,
      }),
    })
  )
  xAxis.get('renderer').grid.template.set('visible', false)
  xAxis.get('renderer').labels.template.setAll({
    ...AXIS_LABEL_STYLE,
    paddingTop: 30,
  })
  xAxis.set('dateFormats', axisConfig.dateFormats)

  const yAxis = chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      renderer: am5xy.AxisRendererY.new(root, {}),
      maxPrecision: 0,
    })
  )
  yAxis.set(
    'numberFormatter',
    am5.NumberFormatter.new(root, {
      numberFormat: '#a',
    })
  )
  yAxis.get('renderer').labels.template.setAll({
    ...AXIS_LABEL_STYLE,
    paddingRight: 10,
  })
  yAxis.get('renderer').grid.template.setAll({
    stroke: am5.color('#E2E8F0'),
    strokeOpacity: 1,
    strokeDasharray: [4, 4],
  })

  if (isMultiSeriesBreakout(activeView)) {
    addBreakoutSeries(root, chart, xAxis, yAxis, seriesData)
  } else {
    addOverallSeries(root, chart, xAxis, yAxis, overallData, shipmentsLabel)
  }

  chart.zoomOutButton.set('forceHidden', true)
  chart.appear(800, 100)

  return () => root.dispose()
}
